import { Injectable } from '@angular/core';

export interface PhoneValidationResult {
  isValid: boolean;
  errorMessage?: string;
  countryCode?: string;
}

export interface CountryPhoneRule {
  code: string;
  name: string;
  dialCode: string;
  pattern: RegExp;
  minLength: number;
  maxLength: number;
  example: string;
  flagUrl: string;
}

@Injectable({
  providedIn: 'root'
})
export class PhoneValidatorService {
  
  private phoneRules: CountryPhoneRule[] = [
    {
      code: 'IT',
      name: 'Italy',
      dialCode: '+39',
      pattern: /^[0-9]{6,11}$/,
      minLength: 6,
      maxLength: 11,
      example: '123 456 7890',
      flagUrl: 'https://flagcdn.com/w20/it.png'
    },
    {
      code: 'GB',
      name: 'United Kingdom',
      dialCode: '+44',
      pattern: /^[0-9]{10,11}$/,
      minLength: 10,
      maxLength: 11,
      example: '1234 567 890',
      flagUrl: 'https://flagcdn.com/w20/gb.png'
    },
    {
      code: 'MD',
      name: 'Moldova',
      dialCode: '+373',
      pattern: /^[0-9]{8}$/,
      minLength: 8,
      maxLength: 8,
      example: '1234 5678',
      flagUrl: 'https://flagcdn.com/w20/md.png'
    },
    {
      code: 'AL',
      name: 'Albania',
      dialCode: '+355',
      pattern: /^[0-9]{8,9}$/,
      minLength: 8,
      maxLength: 9,
      example: '123 456 789',
      flagUrl: 'https://flagcdn.com/w20/al.png'
    },
    {
      code: 'PL',
      name: 'Poland',
      dialCode: '+48',
      pattern: /^[0-9]{9}$/,
      minLength: 9,
      maxLength: 9,
      example: '123 456 789',
      flagUrl: 'https://flagcdn.com/w20/pl.png'
    },
    {
      code: 'BG',
      name: 'Bulgaria',
      dialCode: '+359',
      pattern: /^[0-9]{8,9}$/,
      minLength: 8,
      maxLength: 9,
      example: '123 456 789',
      flagUrl: 'https://flagcdn.com/w20/bg.png'
    }
  ];

  getCountryRules(): CountryPhoneRule[] {
    return this.phoneRules;
  }

  getCountryByCode(countryCode: string): CountryPhoneRule | undefined {
    return this.phoneRules.find(rule => rule.code === countryCode);
  }

  getCountryByDialCode(dialCode: string): CountryPhoneRule | undefined {
    return this.phoneRules.find(rule => rule.dialCode === dialCode);
  }

  validatePhoneNumber(phoneNumber: string, countryCode: string): PhoneValidationResult {
    // Se il numero è vuoto, NON è valido (obbligatorio)
    if (!phoneNumber || phoneNumber.trim() === '') {
      return { 
        isValid: false, 
        errorMessage: 'field.telefono.invalid',
        countryCode 
      };
    }

    const country = this.getCountryByCode(countryCode);
    if (!country) {
      return {
        isValid: false,
        errorMessage: 'field.telefono.invalid',
        countryCode
      };
    }

    // Rimuovi spazi e caratteri speciali per la validazione
    const cleanNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');

    // Controlla se il numero rispetta il pattern del paese
    if (!country.pattern.test(cleanNumber)) {
      return {
        isValid: false,
        errorMessage: 'field.telefono.invalid',
        countryCode
      };
    }

    // Controlla la lunghezza
    if (cleanNumber.length < country.minLength || cleanNumber.length > country.maxLength) {
      return {
        isValid: false,
        errorMessage: 'field.telefono.invalid',
        countryCode
      };
    }

    return {
      isValid: true,
      countryCode
    };
  }

  validateFullPhoneNumber(fullNumber: string): PhoneValidationResult {
    if (!fullNumber || fullNumber.trim() === '') {
      return { 
        isValid: false, 
        errorMessage: 'field.telefono.invalid' 
      };
    }

    // Trova il paese dal prefisso
    const country = this.phoneRules.find(rule => fullNumber.startsWith(rule.dialCode));
    if (!country) {
      return {
        isValid: false,
        errorMessage: 'Invalid country code'
      };
    }

    // Estrai il numero senza prefisso
    const phoneNumber = fullNumber.substring(country.dialCode.length);
    return this.validatePhoneNumber(phoneNumber, country.code);
  }

  getValidationErrorMessage(countryCode: string): string {
    return 'field.telefono.invalid';
  }
}